import { useEffect, useMemo, useState } from 'react';
import type { EmployeeSession } from '../../types/employee';
import type { OfficeAutoLogout, OfficeAutoLogoutReason } from './workforceTypes';

const OFFICE_IDLE_MS = 60_000;
const OFFICE_POST_PUNCH_MS = 15_000;

export function useOfficeAutoLogout(
  session: EmployeeSession | null,
  logout: () => void
): OfficeAutoLogout | null {
  const [idleDeadline, setIdleDeadline] = useState<number | null>(null);
  const [postPunchDeadline, setPostPunchDeadline] = useState<number | null>(null);
  const [logoutTick, setLogoutTick] = useState(0);

  useEffect(() => {
    if (!session || session.channel !== 'office') {
      setIdleDeadline(null);
      setPostPunchDeadline(null);
      return;
    }
    setIdleDeadline(Date.now() + OFFICE_IDLE_MS);
    setPostPunchDeadline(null);
  }, [session?.employeeId, session?.channel]);

  useEffect(() => {
    if (!session || session.channel !== 'office') return;

    const resetIdle = () => setIdleDeadline(Date.now() + OFFICE_IDLE_MS);
    window.addEventListener('pointerdown', resetIdle);
    window.addEventListener('keydown', resetIdle);

    return () => {
      window.removeEventListener('pointerdown', resetIdle);
      window.removeEventListener('keydown', resetIdle);
    };
  }, [session]);

  useEffect(() => {
    const onPunch = () => setPostPunchDeadline(Date.now() + OFFICE_POST_PUNCH_MS);
    window.addEventListener('garderie:punch', onPunch);
    return () => window.removeEventListener('garderie:punch', onPunch);
  }, []);

  useEffect(() => {
    if (!session || session.channel !== 'office') return;
    const timer = window.setInterval(() => setLogoutTick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [session]);

  const officeAutoLogout = useMemo((): OfficeAutoLogout | null => {
    if (!session || session.channel !== 'office' || idleDeadline === null) return null;
    void logoutTick;

    const now = Date.now();
    const candidates: { ms: number; reason: OfficeAutoLogoutReason }[] = [];
    if (idleDeadline > now) {
      candidates.push({ ms: idleDeadline - now, reason: 'idle' });
    }
    if (postPunchDeadline && postPunchDeadline > now) {
      candidates.push({ ms: postPunchDeadline - now, reason: 'post-punch' });
    }
    if (candidates.length === 0) {
      return { remainingSeconds: 0, reason: 'idle' };
    }

    const soonest = candidates.reduce((a, b) => (a.ms < b.ms ? a : b));
    return {
      remainingSeconds: Math.max(0, Math.ceil(soonest.ms / 1000)),
      reason: soonest.reason,
    };
  }, [session, idleDeadline, postPunchDeadline, logoutTick]);

  useEffect(() => {
    if (!officeAutoLogout || officeAutoLogout.remainingSeconds > 0) return;
    logout();
  }, [officeAutoLogout, logout]);

  return officeAutoLogout;
}
